import { Button, Card, Fieldset, Group, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { FORMAT_RULES } from '@quizee/core';
import {
  newOutcomeDraft,
  newQuestionDraft,
  withOutcomeRemoved,
  type InteractionDraft,
  type OutcomeDraft,
  type QuestionDraft,
} from '../interaction/draft';
import { moveInList } from '../interaction/reorder';
import type { NewsletterContext } from '../interaction/newsletter';
import { MoveButtons } from './MoveButtons';
import { OutcomesEditor } from './OutcomesEditor';
import { QuestionEditor } from './QuestionEditor';
import { SignupSection } from './SignupSection';

/**
 * Everything an editor fills in, for any format.
 *
 * The create page and the editor both mount this, so both ask for the same things in the same
 * order, and what appears is decided by the format's rules rather than by which page you are on: a
 * format that allows one question does not offer a question list, a format with no reader facing
 * results asks for a finishing message instead, a personality quiz gets its outcomes.
 *
 * The newsletter step is the one part that needs something from outside the interaction: the
 * masthead's wording, and what the masthead does with an address. A screen that cannot answer
 * those passes nothing and the step is not offered there, which is the honest thing to do rather
 * than showing a switch beside a sentence we would have had to invent.
 */
export function InteractionFields({
  draft,
  onChange,
  disabled = false,
  newsletter = null,
}: {
  draft: InteractionDraft;
  onChange: (draft: InteractionDraft) => void;
  disabled?: boolean;
  /** The brand's newsletter settings, or null on a screen that has not read them. */
  newsletter?: NewsletterContext | null;
}) {
  const rules = FORMAT_RULES[draft.format];
  const name = rules.label.toLowerCase();
  const single = rules.maxQuestions === 1;

  function setQuestion(key: string, next: QuestionDraft) {
    onChange({
      ...draft,
      questions: draft.questions.map((question) => (question.key === key ? next : question)),
    });
  }

  function moveQuestion(index: number, delta: number) {
    // The questions themselves move; nothing is rebuilt, so every question and option id survives
    // the reorder and the answers already counted against them stay attached.
    onChange({ ...draft, questions: moveInList(draft.questions, index, delta) });
  }

  function addQuestion() {
    const type = draft.questions[draft.questions.length - 1]?.type ?? rules.questionTypes[0];
    onChange({
      ...draft,
      questions: [...draft.questions, newQuestionDraft(type ?? 'single_choice', draft.format)],
    });
  }

  function removeQuestion(key: string) {
    onChange({
      ...draft,
      questions: draft.questions.filter((question) => question.key !== key),
    });
  }

  function updateOutcome(key: string, patch: Partial<OutcomeDraft>) {
    onChange({
      ...draft,
      outcomes: draft.outcomes.map((outcome) =>
        outcome.key === key ? { ...outcome, ...patch } : outcome,
      ),
    });
  }

  return (
    <Fieldset variant="unstyled" disabled={disabled}>
      <Stack gap="md">
        <TextInput
          id="interaction-title"
          label="Title"
          description={`Shown above the ${name} in the article.`}
          value={draft.title}
          maxLength={300}
          onChange={(event) => onChange({ ...draft, title: event.target.value })}
        />

        {!single && (
          <Textarea
            id="interaction-introduction"
            label="Introduction"
            description="Optional. A line or two above the first question."
            autosize
            minRows={2}
            maxRows={6}
            value={draft.introduction}
            maxLength={2000}
            onChange={(event) => onChange({ ...draft, introduction: event.target.value })}
          />
        )}

        {rules.requiresOutcomes && (
          <OutcomesEditor
            outcomes={draft.outcomes}
            onUpdate={updateOutcome}
            onAdd={() => onChange({ ...draft, outcomes: [...draft.outcomes, newOutcomeDraft()] })}
            onRemove={(key) => onChange(withOutcomeRemoved(draft, key))}
            onMove={(index, delta) =>
              onChange({ ...draft, outcomes: moveInList(draft.outcomes, index, delta) })
            }
          />
        )}

        {single ? (
          <QuestionEditor
            question={firstQuestion(draft)}
            index={0}
            format={draft.format}
            rules={rules}
            outcomes={draft.outcomes}
            onChange={(next) => setQuestion(next.key, next)}
          />
        ) : (
          <section aria-labelledby="questions-heading">
            <Text component="h3" id="questions-heading" fw={500} size="sm">
              Questions
            </Text>
            <Text c="dimmed" size="xs" mb="xs">
              Readers answer these in order. Move one up or down to change that order.
            </Text>

            <Stack gap="sm">
              {draft.questions.map((question, index) => (
                <Card
                  key={question.key}
                  withBorder
                  radius="md"
                  p="md"
                  role="group"
                  aria-label={`Question ${String(index + 1)}`}
                >
                  <Group justify="space-between" align="center" gap="sm" mb="xs" wrap="wrap">
                    <Text component="h4" fw={600} size="sm">
                      Question {index + 1}
                    </Text>
                    <Group gap="xs" wrap="nowrap">
                      <MoveButtons
                        what={`question ${String(index + 1)}`}
                        index={index}
                        total={draft.questions.length}
                        onMove={moveQuestion}
                      />
                      <Button
                        variant="subtle"
                        color="gray"
                        size="compact-sm"
                        onClick={() => removeQuestion(question.key)}
                        disabled={draft.questions.length <= rules.minQuestions}
                        aria-label={`Remove question ${String(index + 1)}`}
                      >
                        Remove
                      </Button>
                    </Group>
                  </Group>
                  <QuestionEditor
                    question={question}
                    index={index}
                    format={draft.format}
                    rules={rules}
                    outcomes={draft.outcomes}
                    onChange={(next) => setQuestion(question.key, next)}
                  />
                </Card>
              ))}
            </Stack>

            <Button
              variant="default"
              size="compact-sm"
              mt="sm"
              onClick={addQuestion}
              disabled={draft.questions.length >= rules.maxQuestions}
            >
              Add question
            </Button>
          </section>
        )}

        {newsletter !== null && (
          <SignupSection
            signup={draft.signup}
            onChange={(signup) => onChange({ ...draft, signup })}
            newsletter={newsletter}
          />
        )}

        {!rules.hasResults && (
          <Textarea
            id="interaction-completion"
            label="Finishing message"
            description={`Optional. Readers see this when they have finished. A ${name} shows them no results.`}
            autosize
            minRows={2}
            maxRows={6}
            value={draft.completionMessage}
            maxLength={2000}
            onChange={(event) => onChange({ ...draft, completionMessage: event.target.value })}
          />
        )}
      </Stack>
    </Fieldset>
  );
}

/**
 * A format that allows exactly one question always has one: emptyDraft and draftFromContent both
 * guarantee it. This keeps that guarantee explicit rather than reaching for an index and hoping.
 */
function firstQuestion(draft: InteractionDraft): QuestionDraft {
  const question = draft.questions[0];
  if (question !== undefined) return question;
  return newQuestionDraft('single_choice');
}
